import { useState, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover'
import { Clock, CalendarDays } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { ScreenerTimeframe } from '@/types'

const RELATIVE_PRESETS = [
    { label: '30m', seconds: 30 * 60 },
    { label: '1h', seconds: 60 * 60 },
    { label: '4h', seconds: 4 * 60 * 60 },
    { label: '12h', seconds: 12 * 60 * 60 },
    { label: '24h', seconds: 24 * 60 * 60 },
    { label: '3d', seconds: 3 * 24 * 60 * 60 },
    { label: '7d', seconds: 7 * 24 * 60 * 60 },
    { label: '30d', seconds: 30 * 86400 },
    { label: '90d', seconds: 90 * 86400 },
    { label: '180d', seconds: 180 * 86400 },
    { label: '1y', seconds: 365 * 86400 },
] as const

interface TimeframeSelectorProps {
    value?: ScreenerTimeframe
    onChange: (timeframe: ScreenerTimeframe) => void
}

function toUtcDatetimeString(ts: number): string {
    const d = new Date(ts * 1000)
    const year = d.getUTCFullYear()
    const month = String(d.getUTCMonth() + 1).padStart(2, '0')
    const day = String(d.getUTCDate()).padStart(2, '0')
    const hours = String(d.getUTCHours()).padStart(2, '0')
    const minutes = String(d.getUTCMinutes()).padStart(2, '0')
    return `${year}-${month}-${day}T${hours}:${minutes}`
}

function fromUtcDatetimeString(str: string): number {
    return Math.floor(new Date(`${str}Z`).getTime() / 1000)
}

export function TimeframeSelector({ value, onChange }: TimeframeSelectorProps) {
    const [open, setOpen] = useState(false)

    const mode = value?.mode || 'live'
    const isRelative = mode === 'relative'
    const isAbsolute = mode === 'absolute'
    const hasRange = isRelative || isAbsolute

    const activeLabel = useMemo(() => {
        if (isRelative && value?.relative_seconds) {
            const match = RELATIVE_PRESETS.find((p) => p.seconds === value.relative_seconds)
            return match ? match.label : `${Math.round(value.relative_seconds / 60)}m`
        }
        if (isAbsolute) return 'Custom'
        return 'Time range'
    }, [value, isRelative, isAbsolute])

    const handleReset = () => {
        onChange({ mode: 'live' })
        setOpen(false)
    }

    const handleRelative = (seconds: number) => {
        onChange({ mode: 'relative', relative_seconds: seconds })
        setOpen(false)
    }

    const defaultFrom = useMemo(() => {
        if (isAbsolute && value?.from_ts) return toUtcDatetimeString(value.from_ts)
        return toUtcDatetimeString(Math.floor(Date.now() / 1000) - 24 * 60 * 60)
    }, [isAbsolute, value?.from_ts])

    const defaultTo = useMemo(() => {
        if (isAbsolute && value?.to_ts) return toUtcDatetimeString(value.to_ts)
        return toUtcDatetimeString(Math.floor(Date.now() / 1000))
    }, [isAbsolute, value?.to_ts])

    const [fromValue, setFromValue] = useState(defaultFrom)
    const [toValue, setToValue] = useState(defaultTo)
    const fromSeconds = fromUtcDatetimeString(fromValue)
    const toSeconds = fromUtcDatetimeString(toValue)
    const validRange = Number.isFinite(fromSeconds) && Number.isFinite(toSeconds) && toSeconds > fromSeconds

    const handleAbsoluteChange = (fromStr: string, toStr: string) => {
        const from_ts = fromUtcDatetimeString(fromStr)
        const to_ts = fromUtcDatetimeString(toStr)
        if (from_ts && to_ts && to_ts > from_ts) {
            onChange({ mode: 'absolute', from_ts, to_ts })
            setOpen(false)
        }
    }

    return (
        <div className="flex items-center gap-1">
            <Popover open={open} onOpenChange={(next) => {
                if (next) { setFromValue(defaultFrom); setToValue(defaultTo) }
                setOpen(next)
            }}>
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        size="sm"
                        className={cn(
                            'h-7 text-xs border-border/50 bg-muted/10 gap-1.5',
                            hasRange && 'border-primary/50 bg-primary/5 text-primary'
                        )}
                    >
                        {isAbsolute ? (
                            <CalendarDays className="h-3 w-3" />
                        ) : (
                            <Clock className="h-3 w-3" />
                        )}
                        <span>{activeLabel}</span>
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-3" align="start">
                    <div className="space-y-3">
                        {/* Relative presets */}
                        <div className="space-y-1.5">
                            <span className="text-[10px] font-semibold uppercase text-muted-foreground">
                                Relative
                            </span>
                            <div className="flex flex-wrap gap-1.5">
                                {RELATIVE_PRESETS.map((preset) => (
                                    <Button
                                        type="button"
                                        key={preset.label}
                                        variant={
                                            isRelative && value?.relative_seconds === preset.seconds
                                                ? 'default'
                                                : 'outline'
                                        }
                                        className="h-7 cursor-pointer font-normal text-xs px-2 py-0.5"
                                        onClick={() => handleRelative(preset.seconds)}
                                    >
                                        {preset.label}
                                    </Button>
                                ))}
                            </div>
                        </div>

                        {/* Absolute */}
                        <div className="space-y-1.5">
                            <span className="text-[10px] font-semibold uppercase text-muted-foreground">
                                Custom Range (UTC)
                            </span>
                            <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1.5 items-center">
                                <span className="text-[11px] text-muted-foreground">From</span>
                                <input
                                    type="datetime-local"
                                    className="h-7 text-xs px-2 rounded border border-border/50 bg-background focus:outline-none focus:ring-1 focus:ring-primary/50 w-full"
                                    aria-label="Range start (UTC)"
                                    value={fromValue}
                                    onChange={(e) => setFromValue(e.target.value)}
                                    onInput={(e) => setFromValue(e.currentTarget.value)}
                                />
                                <span className="text-[11px] text-muted-foreground">To</span>
                                <input
                                    type="datetime-local"
                                    data-role="to"
                                    className="h-7 text-xs px-2 rounded border border-border/50 bg-background focus:outline-none focus:ring-1 focus:ring-primary/50 w-full"
                                    aria-label="Range end (UTC)"
                                    value={toValue}
                                    onChange={(e) => setToValue(e.target.value)}
                                    onInput={(e) => setToValue(e.currentTarget.value)}
                                />
                            </div>
                        </div>

                        <p className="text-[11px] text-muted-foreground">Choose a starting view, then drag freely through stored history. Wider and older views use the available resolution.</p>
                        <Button size="sm" className="h-7 text-xs" disabled={!validRange}
                            onClick={() => handleAbsoluteChange(fromValue, toValue)}>Apply range</Button>
                        {!validRange && <p role="alert" className="text-[11px] text-amber-500">Enter a valid end time after the start time.</p>}
                        {hasRange && (
                            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={handleReset}>
                                Reset range
                            </Button>
                        )}
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    )
}
